import React from 'react';
import ModernTimeDropdown from '../layouts/modernDropdown';

export const classifierLabels = (item) => {
  const type = item.output_type || item.type;
  return type === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : (item.possible_values || item.options || []);
};

export const supportsClassifier = (item) => ['Yes/No/NA', 'Categorical', 'Categorical - Single Value', 'Number'].includes(item.output_type || item.type);

export const classifierOutputOptions = (kind, options) => kind === 'classifier'
  ? options.filter(type => supportsClassifier({ type }))
  : options;

// Used by save, sample, upload/download and version reload paths.
export const classifierSettings = (item) => {
  if (item.subparameter_type !== 'classifier') {
    return item.subparameter_type ? { subparameter_type: item.subparameter_type } : {};
  }
  const settings = { subparameter_type: 'classifier' };
  if ((item.output_type || item.type) === 'Number') {
    settings.classifier_score_max = item.classifier_score_max;
  } else if (item.classifier_criteria) {
    // Preserve explicit legacy descriptions. New fields use their instruction only.
    const criteria = Object.fromEntries(classifierLabels(item)
      .filter(label => typeof item.classifier_criteria[label] === 'string' && item.classifier_criteria[label].trim())
      .map(label => [label, item.classifier_criteria[label]]));
    if (Object.keys(criteria).length) settings.classifier_criteria = criteria;
  }
  return settings;
};

export const classifierError = (item) => {
  if (item.subparameter_type !== 'classifier') return '';
  if (!supportsClassifier(item)) {
    return 'Classifier supports only Yes/No/NA, single-value Categorical, and Number ratings. Keep LLM Based for this output type.';
  }
  if (!(item.instruction || item.definition || '').trim()) return 'Enter the answer rules in the Definition field.';
  if ((item.output_type || item.type) === 'Number') {
    return Number.isInteger(item.classifier_score_max) && item.classifier_score_max >= 1 && item.classifier_score_max <= 9
      ? '' : 'Select a maximum score from 1 to 9. The minimum is 0.';
  }
  const labels = classifierLabels(item);
  if (labels.length < 2 || new Set(labels).size !== labels.length || labels.some(label => typeof label !== 'string' || !label.trim())) {
    return 'Classifier requires at least two unique, non-empty labels.';
  }
  return '';
};

const processingTypes = {
  llm_based: 'LLM Based',
  logic_based: 'Logic Based',
  classifier: 'Classifier Based',
};

export default function ClassifierFields({ value, onChange, allowLogic = false, existingKind }) {
  const kind = value.subparameter_type || 'llm_based';
  const supported = supportsClassifier(value);
  const error = classifierError(value);
  const existingLogic = allowLogic && existingKind === 'logic_based';
  const availableKinds = [
    'llm_based',
    ...(allowLogic && (!existingKind || existingLogic) ? ['logic_based'] : []),
    ...(supported ? ['classifier'] : []),
  ];
  const settings = {
    subparameter_type: kind,
    ...(value.classifier_criteria ? { classifier_criteria: value.classifier_criteria } : {}),
    ...(value.classifier_score_max !== undefined ? { classifier_score_max: value.classifier_score_max } : {}),
  };
  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 text-sm font-medium text-gray-700">Processing type</p>
        <ModernTimeDropdown
          width="w-full"
          ariaLabel="Processing type"
          value={processingTypes[kind]}
          isDisabled={existingLogic}
          options={availableKinds.map(option => processingTypes[option])}
          onChange={event => {
            const selectedKind = availableKinds.find(option => processingTypes[option] === event.target.value);
            if (existingLogic || !selectedKind) return;
            onChange({ ...settings, subparameter_type: selectedKind });
          }}
        />
      </div>
      {allowLogic && existingKind && <p className="text-sm text-gray-500">Existing fields can switch between LLM Based and Classifier Based when supported. Changing to or from Logic Based is unavailable because other fields can depend on its execution order.</p>}
      {!supported && <p className="text-sm text-gray-500">Classifier Based is unavailable for this output type. It supports Yes/No/NA, single-value Categorical, and Number ratings.</p>}
      {kind === 'classifier' && <>
        <p className="text-sm text-gray-500">Use the Definition field to describe how to choose the answer. An explanation is generated automatically.</p>
        {(value.output_type || value.type) === 'Number' && <>
          <div className="flex gap-4">
            <label className="block text-sm font-medium text-gray-700">
              Minimum score
              <input aria-label="Minimum score" value="0" readOnly className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md global-input" />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Maximum score
              <select required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md global-input"
                value={value.classifier_score_max ?? ''}
                onChange={event => onChange({ ...settings, classifier_score_max: event.target.value === '' ? undefined : Number(event.target.value) })}>
                <option value="">Select maximum</option>
                {Array.from({ length: 9 }, (_, index) => index + 1).map(maximum => <option key={maximum} value={maximum}>{maximum}</option>)}
              </select>
            </label>
          </div>
          <p className="text-sm text-gray-500">Returns a rating from 0 to your selected maximum. Decimal scores are possible. Describe the ordered scoring levels in the Definition field. Use LLM Based to extract age, price, duration, or other exact values.</p>
        </>}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </>}
    </div>
  );
}
